/**
 * Custom Board Detail — the actual Kanban.
 *
 * Uses the shared KanbanColumn + MoveToDropdown so every custom board
 * matches the visual language of Lead CRM / Support / Accounts / Orders.
 * Inline card creation on each column, inline card editing via a modal,
 * inline column rename (owner only).
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Layers, Loader2, Plus, Trash2, Settings, X, Flame, Zap, Clock, User, Filter, MessageCircle, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import { KanbanColumn, MoveToDropdown, KANBAN_PALETTE } from '@/components/kanban/KanbanShared';

const PRIORITY_META = {
  urgent:   { icon: Flame, chip: 'bg-rose-100 text-rose-800',   swatch: '#F43F5E' },
  priority: { icon: Zap,   chip: 'bg-amber-100 text-amber-800', swatch: '#F59E0B' },
  standard: { icon: Clock, chip: 'bg-slate-100 text-slate-700', swatch: '#64748B' },
};
const PALETTE_ARR = Object.entries(KANBAN_PALETTE);

// 2-letter initials — used on the small assignee chip
const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function CustomBoardDetail() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const homePath = ['admin', 'manager', 'team'].includes(user?.role) ? '/staff' : '/';
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCard, setOpenCard] = useState(null);   // { card } editing modal
  const [commentsCard, setCommentsCard] = useState(null);  // card whose comments panel is open
  const [addingTo, setAddingTo] = useState(null);   // column_id for the "New card" modal
  const [showSettings, setShowSettings] = useState(false);
  const [assignees, setAssignees] = useState([]);   // [{user_id, name, role}, ...]
  const [assigneeFilter, setAssigneeFilter] = useState('');  // '', 'me', or user_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Board not found');
      setBoard(await r.json());
    } catch (e) { toast.error(e.message); navigate('/boards'); }
    finally { setLoading(false); }
  }, [boardId, navigate]);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Assignees list (staff roster) — small dataset, fetched once.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/boards/assignees`, { credentials: 'include' });
        if (r.ok) setAssignees(await r.json());
      } catch (_) { /* non-fatal */ }
    })();
  }, [user]);

  // { user_id -> name } for card chips
  const assigneeMap = useMemo(() => {
    const m = {};
    assignees.forEach((a) => { m[a.user_id] = a.name; });
    return m;
  }, [assignees]);

  const isOwner = board && (board.created_by === user?.user_id || ['admin', 'manager'].includes(user?.role));

  // Card filter: honour the header "Assigned to" chip
  const cardMatchesFilter = useCallback((c) => {
    if (!assigneeFilter) return true;
    if (assigneeFilter === 'me') return c.assigned_to === user?.user_id;
    return c.assigned_to === assigneeFilter;
  }, [assigneeFilter, user?.user_id]);

  const cardsByCol = useMemo(() => {
    const g = {};
    (board?.columns || []).forEach((c) => { g[c.id] = []; });
    (board?.cards || []).forEach((c) => {
      if (g[c.column_id] && cardMatchesFilter(c)) g[c.column_id].push(c);
    });
    return g;
  }, [board, cardMatchesFilter]);

  const moveOptions = (board?.columns || []).map((c) => ({ key: c.id, label: c.label }));

  const patchBoard = async (body) => {
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Save failed');
      setBoard(await r.json());
      toast.success('Board updated');
    } catch (err) { toast.error(err.message); }
  };

  const addColumn = async () => {
    const label = window.prompt('New column name?');
    if (!label) return;
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/columns`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, colour: KANBAN_PALETTE.slate }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const removeColumn = async (col) => {
    if (!window.confirm(`Delete "${col.label}" and all its cards?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/columns/${col.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const patchColumn = async (col, body) => {
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/columns/${col.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const addCard = async (columnId, body) => {
    const payload = { column_id: columnId, title: (body?.title || '').trim() };
    if (!payload.title) return;
    if (body?.description) payload.description = body.description;
    if (body?.priority) payload.priority = body.priority;
    if (body?.assigned_to) payload.assigned_to = body.assigned_to;
    if (body?.due_date) payload.due_date = body.due_date;
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/cards`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      const card = await r.json();
      setBoard((b) => ({ ...b, cards: [...(b.cards || []), card] }));
      setAddingTo(null);
    } catch (err) { toast.error(err.message); }
  };

  const moveCard = async (card, targetColumnId) => {
    if (card.column_id === targetColumnId) return;
    // optimistic update
    setBoard((b) => ({
      ...b,
      cards: b.cards.map((c) => c.id === card.id ? { ...c, column_id: targetColumnId } : c),
    }));
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/cards/${card.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ column_id: targetColumnId }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Move failed');
      const label = board.columns.find((c) => c.id === targetColumnId)?.label;
      toast.success(`Moved to ${label}`);
    } catch (err) { toast.error(err.message); load(); }
  };

  const patchCard = async (cardId, body) => {
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/cards/${cardId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      const fresh = await r.json();
      setBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === cardId ? fresh : c) }));
      return fresh;
    } catch (err) { toast.error(err.message); }
  };

  const deleteCard = async (card) => {
    if (!window.confirm(`Delete card "${card.title}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/cards/${card.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      setBoard((b) => ({ ...b, cards: b.cards.filter((c) => c.id !== card.id) }));
      setOpenCard(null);
    } catch (err) { toast.error(err.message); }
  };

  // Merge a freshly-fetched card back into local state and keep both the
  // Edit modal and the Comments modal in sync (they can be open at once).
  const mergeCard = (fresh) => {
    setBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === fresh.id ? fresh : c) }));
    setCommentsCard((cur) => (cur && cur.id === fresh.id ? fresh : cur));
    setOpenCard((cur) => (cur && cur.id === fresh.id ? fresh : cur));
  };

  const addComment = async (card, body) => {
    if (!body.trim()) return null;
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/cards/${card.id}/comments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: body.trim() }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      const fresh = await r.json();
      mergeCard(fresh);
      return fresh;
    } catch (err) { toast.error(err.message); return null; }
  };

  const deleteComment = async (card, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const r = await fetch(`${API_URL}/api/boards/${boardId}/cards/${card.id}/comments/${commentId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      const fresh = await r.json();
      mergeCard(fresh);
    } catch (err) { toast.error(err.message); }
  };

  if (loading || !board) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white sticky top-0 z-20" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-[1600px] mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate('/boards')} data-testid="board-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Layers className="w-5 h-5 shrink-0" /> {board.name}
            </h1>
            <p className="text-white/70 text-xs font-mono truncate">{board.reference}</p>
          </div>
          {isOwner && (
            <Button
              onClick={() => setShowSettings(true)}
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
              data-testid="board-settings"
              aria-label="Board settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4">
        {/* Assignee filter — quick "Only mine" chip + full dropdown */}
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600">
            <Filter className="w-3.5 h-3.5" /> Assigned to
          </div>
          <button
            type="button"
            onClick={() => setAssigneeFilter('')}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${
              assigneeFilter === '' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
            }`}
            data-testid="board-filter-all"
          >All</button>
          <button
            type="button"
            onClick={() => setAssigneeFilter('me')}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${
              assigneeFilter === 'me' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
            }`}
            data-testid="board-filter-me"
          >Only mine</button>
          <select
            value={['', 'me'].includes(assigneeFilter) ? '' : assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="text-xs border rounded-md px-2 py-1 bg-white"
            data-testid="board-filter-select"
          >
            <option value="">— Pick a person —</option>
            {assignees.map((a) => (
              <option key={a.user_id} value={a.user_id}>{a.name}</option>
            ))}
          </select>
          {assigneeFilter && (
            <span className="text-[11px] text-slate-500">
              Showing {(board.cards || []).filter(cardMatchesFilter).length} of {(board.cards || []).length} cards
            </span>
          )}
        </div>

        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {board.columns.map((col) => {
              const rows = cardsByCol[col.id] || [];
              return (
                <KanbanColumn
                  key={col.id}
                  column={{ ...col, key: col.id, colour: col.colour }}
                  count={rows.length}
                  testId={`board-col-${col.id}`}
                  emptyLabel={'No cards yet'}
                >
                  {rows.map((c) => {
                    const p = c.priority ? PRIORITY_META[c.priority] : null;
                    const PIcon = p?.icon;
                    return (
                      <div
                        key={c.id}
                        onClick={() => setOpenCard(c)}
                        className="group bg-white border rounded-lg p-3 shadow-sm hover:shadow-md cursor-pointer transition"
                        style={p ? { borderLeft: `4px solid ${p.swatch}` } : {}}
                        data-testid={`board-card-${c.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="font-semibold text-sm text-gray-900 flex-1 min-w-0 line-clamp-2">{c.title}</div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteCard(c); }}
                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-rose-600 p-0.5 -mr-0.5 -mt-0.5 rounded"
                            aria-label="Delete card"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {c.description && (
                          <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{c.description}</p>
                        )}
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          {p && (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.chip}`}>
                              <PIcon className="w-3 h-3" /> {c.priority}
                            </span>
                          )}
                          {c.assigned_to && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 bg-slate-100 rounded-full pr-2 pl-0.5 py-0.5"
                              title={assigneeMap[c.assigned_to] || c.assigned_to}
                            >
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-900 text-white text-[8px]">
                                {initials(assigneeMap[c.assigned_to] || '?')}
                              </span>
                              {assigneeMap[c.assigned_to] || 'Unknown'}
                            </span>
                          )}
                          {c.due_date && (
                            <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                              <Clock className="w-3 h-3" /> {c.due_date}
                            </span>
                          )}
                          {/* Comments — always shown so folks know the icon is clickable */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCommentsCard(c); }}
                            className={`ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition ${
                              (c.comments?.length || 0) > 0
                                ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                : 'text-slate-400 hover:text-indigo-600 hover:bg-slate-100'
                            }`}
                            data-testid={`board-comments-${c.id}`}
                            title={(c.comments?.length || 0) > 0
                              ? `${c.comments.length} comment${c.comments.length === 1 ? '' : 's'} — click to read`
                              : 'Leave a note'}
                          >
                            <MessageCircle className="w-3 h-3" /> {c.comments?.length || 0}
                          </button>
                        </div>
                        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                          <MoveToDropdown
                            current={c.column_id}
                            options={moveOptions}
                            onChange={(target) => moveCard(c, target)}
                            testId={`board-move-${c.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setAddingTo(col.id)}
                    className="w-full text-left text-xs text-slate-500 hover:text-slate-900 hover:bg-white rounded-md px-2 py-2 transition inline-flex items-center gap-1"
                    data-testid={`board-newcard-open-${col.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add a card
                  </button>
                </KanbanColumn>
              );
            })}
            {isOwner && (
              <div className="w-72 flex-shrink-0">
                <button
                  type="button"
                  onClick={addColumn}
                  className="w-full h-full min-h-[100px] rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:text-slate-900 hover:bg-white hover:border-slate-400 transition inline-flex flex-col items-center justify-center gap-1 p-4"
                  data-testid="board-add-column"
                >
                  <Plus className="w-5 h-5" />
                  <span className="text-sm font-semibold">Add column</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card edit modal */}
      {openCard && (
        <CardEditModal
          card={openCard}
          board={board}
          assignees={assignees}
          onClose={() => setOpenCard(null)}
          onSave={async (body) => {
            const fresh = await patchCard(openCard.id, body);
            if (fresh) setOpenCard(null);
          }}
          onDelete={() => deleteCard(openCard)}
        />
      )}

      {/* Add card modal — richer than the old title-only quick-add */}
      {addingTo && (
        <NewCardModal
          board={board}
          columnId={addingTo}
          assignees={assignees}
          onClose={() => setAddingTo(null)}
          onCreate={(body) => addCard(addingTo, body)}
        />
      )}

      {/* Comments modal — dedicated, lightweight; no need to open Edit */}
      {commentsCard && (
        <CommentsModal
          card={commentsCard}
          currentUser={user}
          onClose={() => setCommentsCard(null)}
          onAdd={(body) => addComment(commentsCard, body)}
          onDelete={(commentId) => deleteComment(commentsCard, commentId)}
        />
      )}

      {/* Board settings modal (rename + palette) */}
      {showSettings && (
        <BoardSettingsModal
          board={board}
          onClose={() => setShowSettings(false)}
          onSave={async (body) => { await patchBoard(body); setShowSettings(false); }}
          onColumnPatch={patchColumn}
          onColumnDelete={removeColumn}
        />
      )}
    </div>
  );
}

// ── Card-edit modal ──────────────────────────────────────────────────────────
function CardEditModal({ card, board, assignees, onClose, onSave, onDelete }) {
  const [title, setTitle] = useState(card.title || '');
  const [description, setDescription] = useState(card.description || '');
  const [priority, setPriority] = useState(card.priority || '');
  const [dueDate, setDueDate] = useState(card.due_date || '');
  const [assignedTo, setAssignedTo] = useState(card.assigned_to || '');
  const [saving, setSaving] = useState(false);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="board-card-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold">Edit card</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label htmlFor="ce-title">Title <span className="text-rose-600">*</span></Label>
            <Input id="ce-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="ce-title" />
          </div>
          <div>
            <Label htmlFor="ce-desc">Description</Label>
            <Textarea id="ce-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="ce-description" />
          </div>
          {board.has_priority_levels && (
            <div>
              <Label htmlFor="ce-prio">Priority</Label>
              <select id="ce-prio" value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="ce-priority">
                <option value="">— None —</option>
                <option value="urgent">Urgent</option>
                <option value="priority">Priority</option>
                <option value="standard">Standard</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ce-due">Due date</Label>
              <Input id="ce-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" data-testid="ce-due" />
            </div>
            <div>
              <Label htmlFor="ce-assign">Assigned to</Label>
              <select
                id="ce-assign"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="ce-assignee"
              >
                <option value="">— Unassigned —</option>
                {(assignees || []).map((a) => (
                  <option key={a.user_id} value={a.user_id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t bg-slate-50 rounded-b-xl">
          <Button variant="ghost" onClick={onDelete} className="text-rose-600 hover:bg-rose-50" data-testid="ce-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              disabled={saving || !title.trim()}
              onClick={async () => {
                setSaving(true);
                await onSave({ title, description, priority: priority || null, due_date: dueDate || null, assigned_to: assignedTo || null });
                setSaving(false);
              }}
              data-testid="ce-save"
              className="bg-slate-900 hover:bg-slate-800"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── New-card modal ───────────────────────────────────────────────────────────
// Richer replacement for the old inline title-only quick-add. Same fields as
// the edit modal (minus the delete button) so cards can be captured properly
// the first time round.
function NewCardModal({ board, columnId, assignees, onClose, onCreate }) {
  const colLabel = (board.columns || []).find((c) => c.id === columnId)?.label || 'this column';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onCreate({
        title,
        description,
        priority: priority || null,
        due_date: dueDate || null,
        assigned_to: assignedTo || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="board-newcard-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="font-bold">New card</h3>
            <p className="text-xs text-slate-500">Adding to <span className="font-semibold">{colLabel}</span></p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label htmlFor="nc-title">Title <span className="text-rose-600">*</span></Label>
            <Input
              id="nc-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
              placeholder="What needs doing?"
              className="mt-1"
              data-testid="nc-title"
            />
          </div>
          <div>
            <Label htmlFor="nc-desc">Description</Label>
            <Textarea
              id="nc-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Extra detail, links, context…"
              className="mt-1"
              data-testid="nc-description"
            />
          </div>
          {board.has_priority_levels && (
            <div>
              <Label htmlFor="nc-prio">Priority</Label>
              <select
                id="nc-prio"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="nc-priority"
              >
                <option value="">— None —</option>
                <option value="urgent">Urgent</option>
                <option value="priority">Priority</option>
                <option value="standard">Standard</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nc-due">Due date</Label>
              <Input
                id="nc-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1"
                data-testid="nc-due"
              />
            </div>
            <div>
              <Label htmlFor="nc-assign">Assigned to</Label>
              <select
                id="nc-assign"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="nc-assignee"
              >
                <option value="">— Unassigned —</option>
                {(assignees || []).map((a) => (
                  <option key={a.user_id} value={a.user_id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={saving || !title.trim()}
            onClick={submit}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="nc-add"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Adding…</> : 'Add card'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Board settings modal ─────────────────────────────────────────────────────
function BoardSettingsModal({ board, onClose, onSave, onColumnPatch, onColumnDelete }) {
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description || '');
  const [hasPriority, setHasPriority] = useState(!!board.has_priority_levels);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="board-settings-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold">Board settings</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label htmlFor="bs-name">Board name</Label>
            <Input id="bs-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="bs-name" />
          </div>
          <div>
            <Label htmlFor="bs-desc">Description</Label>
            <Textarea id="bs-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="bs-description" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={hasPriority} onChange={(e) => setHasPriority(e.target.checked)} data-testid="bs-priority-toggle" />
            <span className="text-sm">Cards on this board can carry a priority (Urgent / Priority / Standard)</span>
          </label>

          <div className="border-t pt-3">
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Columns</div>
            <div className="space-y-2">
              {(board.columns || []).map((col) => (
                <div key={col.id} className="flex items-center gap-2">
                  <input
                    type="color"
                    value={col.colour || '#64748B'}
                    onChange={(e) => onColumnPatch(col, { colour: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer"
                    aria-label="Column colour"
                  />
                  <Input
                    defaultValue={col.label}
                    onBlur={(e) => e.target.value !== col.label && onColumnPatch(col, { label: e.target.value })}
                    className="flex-1"
                    data-testid={`bs-col-${col.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => onColumnDelete(col)}
                    disabled={(board.columns || []).length <= 1}
                    className="text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                    title={(board.columns || []).length <= 1 ? 'Keep at least one column' : 'Delete column'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={() => onSave({ name, description, has_priority_levels: hasPriority })}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="bs-save"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Comments modal ───────────────────────────────────────────────────────────
// Lightweight, dedicated to comments so folks can drop a note without going
// through the (heavier) Edit card modal.
function CommentsModal({ card, currentUser, onClose, onAdd, onDelete }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const comments = card.comments || [];
  const isAdmin = ['admin', 'manager'].includes(currentUser?.role);

  const submit = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      const fresh = await onAdd(body);
      if (fresh) setBody('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="board-comments-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8 flex flex-col max-h-[85vh]">
        <div className="flex items-start justify-between px-5 py-3 border-b">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-900 truncate flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-indigo-600 shrink-0" /> Comments
            </h3>
            <p className="text-xs text-slate-500 truncate">on <span className="font-semibold">{card.title}</span></p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded shrink-0" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrolling comment list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {comments.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm italic" data-testid="comments-empty">
              Nothing yet — leave a note to hand off context to the team.
            </div>
          ) : (
            comments.map((c) => {
              const canDelete = c.by === currentUser?.user_id || isAdmin;
              return (
                <div key={c.id} className="group flex gap-3" data-testid={`comment-${c.id}`}>
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                    {(c.by_name || '?').split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs">
                      <span className="font-semibold text-slate-900">{c.by_name || 'Unknown'}</span>
                      <span className="text-slate-400 ml-2">
                        {c.created_at ? new Date(c.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap mt-0.5">{c.body}</p>
                  </div>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete(c.id)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-rose-600 p-1 -m-1 rounded shrink-0"
                      aria-label="Delete comment"
                      data-testid={`comment-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Sticky composer at the bottom */}
        <div className="border-t bg-slate-50 rounded-b-xl px-5 py-3">
          <div className="flex gap-2">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
              }}
              rows={2}
              placeholder="Leave a note for the team… (Cmd/Ctrl+Enter to send)"
              className="flex-1 resize-none bg-white text-sm"
              data-testid="comment-body"
              maxLength={2000}
            />
            <Button
              onClick={submit}
              disabled={sending || !body.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 h-auto"
              data-testid="comment-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

